import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  ArrowLeft, KeyRound, Plus, Pencil, Trash2, Check, X, AlertTriangle,
  Phone, Mail, IdCard, CalendarRange, Wallet, Receipt, StopCircle,
  FileDown, Send, ReceiptText, UserPlus, UserCheck, Copy, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { todayLocalIso } from "@/lib/dateUtils";

const STATUS_BADGE = {
  pending: "bg-amber-100 text-amber-900 border-amber-300",
  active: "bg-emerald-100 text-emerald-800 border-emerald-300",
  ended: "bg-slate-100 text-slate-700 border-slate-300",
  terminated: "bg-red-100 text-red-700 border-red-300",
};

// Common add-on labels coordinators can pick from quickly.
const QUICK_ADD_ONS = ["Utilities", "Trash collection", "Security", "House helper", "Internet"];

const blankLease = (defaultUnit, defaultRC) => ({
  tenant_name: "",
  tenant_email: "",
  tenant_phone: "",
  tenant_id_number: "",
  unit_id: defaultUnit?.id || "",
  start_date: todayLocalIso(),
  end_date: "",
  rate_card_id: defaultRC?.id || null,
  rate_card_name: defaultRC?.name || "",
  monthly_rent: defaultRC?.amount || 0,
  rent_currency: defaultRC?.currency || "KES",
  deposit_amount: 0,
  deposit_currency: "KES",
  deposit_status: "held",
  billing_day: 1,
  add_ons: [],
  status: "active",
  notes: "",
});

export default function Leases() {
  const { user, hasPerm } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [units, setUnits] = useState([]);
  const [rateCards, setRateCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [conflict, setConflict] = useState(null);
  const [filter, setFilter] = useState("active");
  const [genMonth, setGenMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [generating, setGenerating] = useState(false);
  const [sendStatementOpen, setSendStatementOpen] = useState(null);
  const [stmtFrom, setStmtFrom] = useState(() => `${new Date().getFullYear()}-01-01`);
  const [stmtTo, setStmtTo] = useState(() => todayLocalIso());
  const [stmtRecipients, setStmtRecipients] = useState("");
  const [stmtNote, setStmtNote] = useState("");
  const [sendingStmt, setSendingStmt] = useState(false);
  // Phase 46 — tenant portal invites
  const [inviteOpen, setInviteOpen] = useState(null); // lease obj
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [inviteResult, setInviteResult] = useState(null); // {link, mocked, email_sent, expires_at}
  const [portalStatus, setPortalStatus] = useState({}); // { [lease_id]: status }
  const isAdmin = ["admin", "global_admin"].includes(user.role);
  const canInvite = hasPerm("tenants.invite");

  const load = useCallback(async () => {
    try {
      const [ls, un, rc] = await Promise.all([
        api.get("/housing/leases"),
        api.get("/housing/units"),
        api.get("/housing/rate-cards"),
      ]);
      setItems(ls.data);
      setUnits(un.data);
      setRateCards(rc.data);
    } catch { /* */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Phase 46 — fetch portal-status for each lease so the invite button shows
  // the right pill (no login / pending / active). Cheap calls; staggered.
  useEffect(() => {
    if (!canInvite || items.length === 0) return;
    let cancelled = false;
    (async () => {
      const updates = {};
      for (const ls of items) {
        if (cancelled) return;
        try {
          const { data } = await api.get(`/leases/${ls.id}/portal-status`);
          updates[ls.id] = data;
        } catch { /* skip */ }
      }
      if (!cancelled) setPortalStatus((cur) => ({ ...cur, ...updates }));
    })();
    return () => { cancelled = true; };
  }, [items, canInvite]);

  const openInvite = (ls) => {
    setInviteOpen(ls);
    setInviteEmail(ls.tenant_email || "");
    setInviteResult(null);
  };

  const sendInvite = async () => {
    if (!inviteOpen) return;
    if (!inviteEmail || !inviteEmail.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }
    setInviting(true);
    try {
      const { data } = await api.post(`/leases/${inviteOpen.id}/invite-tenant`, {
        email: inviteEmail.toLowerCase().trim(),
        send_email: true,
      });
      setInviteResult(data);
      // Refresh the portal-status pill for this lease
      try {
        const { data: ps } = await api.get(`/leases/${inviteOpen.id}/portal-status`);
        setPortalStatus((cur) => ({ ...cur, [inviteOpen.id]: ps }));
      } catch { /* ignore */ }
      if (data.email_sent) {
        toast.success(`Invite emailed to ${inviteEmail}`);
      } else if (data.mocked) {
        toast.info("Email is mocked on this server — copy the link below to share manually.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invite failed");
    } finally {
      setInviting(false);
    }
  };

  const copyInviteLink = async () => {
    if (!inviteResult?.link) return;
    try {
      await navigator.clipboard.writeText(inviteResult.link);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Could not copy — long-press the link to copy manually");
    }
  };

  const filtered = useMemo(() => {
    if (filter === "all") return items;
    return items.filter((i) => i.status === filter);
  }, [items, filter]);

  const counts = useMemo(() => ({
    active: items.filter((i) => i.status === "active").length,
    pending: items.filter((i) => i.status === "pending").length,
    ended: items.filter((i) => i.status === "ended").length,
    terminated: items.filter((i) => i.status === "terminated").length,
    all: items.length,
  }), [items]);

  // Live conflict check while editing.
  useEffect(() => {
    if (!editing || !editing.unit_id || !editing.start_date) { setConflict(null); return; }
    const t = setTimeout(async () => {
      // We piggyback on POST/PUT: if dates conflict, the create/update returns 409. To keep
      // this dialog responsive we run a lightweight check via the bookings endpoint payload.
      // Simpler: check client-side against current items + bookings would require pulling
      // bookings; instead, defer validation to save and let the backend respond.
      setConflict(null);
    }, 300);
    return () => clearTimeout(t);
  }, [editing]);

  const totalMonthly = useMemo(() => {
    if (!editing) return 0;
    const addOns = (editing.add_ons || []).reduce((s, a) => s + (Number(a.amount) || 0), 0);
    return Number(editing.monthly_rent || 0) + addOns;
  }, [editing]);

  const addAddOn = (label = "") => {
    setEditing({
      ...editing,
      add_ons: [...editing.add_ons, { label, amount: 0, currency: editing.rent_currency || "KES", notes: "" }],
    });
  };
  const updateAddOn = (idx, patch) => {
    const arr = [...editing.add_ons];
    arr[idx] = { ...arr[idx], ...patch };
    setEditing({ ...editing, add_ons: arr });
  };
  const removeAddOn = (idx) => {
    const arr = [...editing.add_ons];
    arr.splice(idx, 1);
    setEditing({ ...editing, add_ons: arr });
  };

  const saveLease = async () => {
    if (!editing.tenant_name?.trim()) { toast.error("Tenant name is required"); return; }
    if (!editing.unit_id) { toast.error("Pick a unit"); return; }
    if (editing.allow_override && !(editing.override_reason || "").trim()) {
      toast.error("Please add a reason for the unit-share override"); return;
    }
    try {
      const payload = {
        ...editing,
        end_date: editing.end_date || null,
        rate_card_id: editing.rate_card_id || null,
      };
      if (editing.id) {
        await api.put(`/housing/leases/${editing.id}`, payload);
      } else {
        await api.post("/housing/leases", payload);
      }
      toast.success("Lease saved");
      setEditing(null);
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (typeof detail === "string" && (detail.includes("overlap") || detail.includes("leased to"))) {
        setConflict(detail);
      }
      toast.error(detail || "Save failed");
    }
  };

  const endLease = async (l, terminated = false) => {
    const verb = terminated ? "TERMINATE" : "End";
    const today = todayLocalIso();
    const end = window.prompt(`${verb} lease for ${l.tenant_name} on what date? (YYYY-MM-DD)`, today);
    if (!end) return;
    try {
      await api.post(`/housing/leases/${l.id}/end?end_date=${end}&terminated=${terminated}`);
      toast.success(`Lease ${terminated ? "terminated" : "ended"}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const deleteLease = async (l) => {
    if (!isAdmin) { toast.error("Only admins can hard-delete a lease"); return; }
    if (!window.confirm(`HARD DELETE lease for ${l.tenant_name}? Use End/Terminate instead unless this was a mistake.`)) return;
    try {
      await api.delete(`/housing/leases/${l.id}`);
      toast.success("Lease deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const generateInvoices = async () => {
    if (!genMonth || !/^\d{4}-\d{2}$/.test(genMonth)) { toast.error("Pick a month"); return; }
    setGenerating(true);
    try {
      const { data } = await api.post("/housing/leases/generate-invoices", { month: genMonth });
      const parts = [];
      if (data.created) parts.push(`${data.created} created`);
      if (data.rebuilt) parts.push(`${data.rebuilt} rebuilt`);
      if (data.skipped) parts.push(`${data.skipped} already existed`);
      const msg = parts.length ? parts.join(" · ") : "No active leases for this month";
      toast.success(`${genMonth} invoices: ${msg}`, { duration: 5000 });
    } catch (e) { toast.error(e.response?.data?.detail || "Generation failed"); }
    finally { setGenerating(false); }
  };

  const downloadStatement = async (l) => {
    const from = window.prompt("Statement FROM date (YYYY-MM-DD)", `${new Date().getFullYear()}-01-01`);
    if (!from) return;
    const to = window.prompt("Statement TO date (YYYY-MM-DD)", todayLocalIso());
    if (!to) return;
    try {
      const r = await api.get(`/housing/leases/${l.id}/statement/pdf?from_date=${from}&to_date=${to}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `statement_${l.tenant_name.replace(/\s+/g, "_")}_${from}_to_${to}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Statement downloaded");
    } catch { toast.error("Download failed"); }
  };

  const openSendStatement = (l) => {
    setSendStatementOpen(l);
    setStmtRecipients(l.tenant_email || "");
    setStmtNote("");
  };

  const sendStatement = async () => {
    const recs = stmtRecipients.split(/[,;\n]/).map((s) => s.trim()).filter(Boolean);
    if (recs.length === 0) { toast.error("Add at least one recipient"); return; }
    setSendingStmt(true);
    try {
      const { data } = await api.post(`/housing/leases/${sendStatementOpen.id}/statement/send`, {
        from_date: stmtFrom, to_date: stmtTo, recipients: recs, note: stmtNote,
      });
      if (data.mocked) {
        toast.info(`Send mocked (RESEND_API_KEY missing) — would go to ${data.recipients.join(", ")}`);
      } else {
        toast.success(`Statement sent to ${data.recipients.join(", ")}`);
      }
      setSendStatementOpen(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
    finally { setSendingStmt(false); }
  };

  const onPickRateCard = (id) => {
    const rc = rateCards.find((r) => r.id === id);
    setEditing({
      ...editing,
      rate_card_id: rc?.id || null,
      rate_card_name: rc?.name || "",
      monthly_rent: rc ? rc.amount : editing.monthly_rent,
      rent_currency: rc ? rc.currency : editing.rent_currency,
    });
  };

  if (loading) return <div className="p-6 text-[#7A8A84]">Loading leases…</div>;

  return (
    <div className="space-y-6" data-testid="leases-page">
      <Button variant="ghost" onClick={() => navigate(-1)} className="text-[#7A8A84]" data-testid="back-btn">
        <ArrowLeft className="h-4 w-4 mr-2" /> Back
      </Button>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#1C1F1E] flex items-center gap-2">
            <KeyRound className="h-6 w-6 text-[#40916C]" /> Leases
          </h1>
          <p className="text-sm text-[#7A8A84]">Long-term residencies billed monthly. Conflicts with bookings are auto-checked.</p>
        </div>
        <Button onClick={() => setEditing(blankLease(units.find((u) => u.active), rateCards.find((r) => r.active)))}
          className="bg-[#1B4332] hover:bg-[#2D6A4F]" data-testid="new-lease-btn">
          <Plus className="h-4 w-4 mr-2" /> New lease
        </Button>
      </div>

      {/* Monthly invoice generator */}
      <Card className="border-[#E8E8E6] bg-[#F4F4F1]/40" data-testid="generator-card">
        <CardContent className="py-4 flex items-center gap-3 flex-wrap">
          <ReceiptText className="h-5 w-5 text-[#1B4332] shrink-0" />
          <div className="flex-1 min-w-[200px]">
            <p className="text-sm font-medium text-[#1C1F1E]">Generate this month's lease invoices</p>
            <p className="text-xs text-[#7A8A84]">One invoice per active lease covering rent + every monthly add-on. Idempotent — running again skips existing invoices.</p>
          </div>
          <Input type="month" value={genMonth} onChange={(e) => setGenMonth(e.target.value)} className="w-44" data-testid="gen-month-input" />
          <Button onClick={generateInvoices} disabled={generating} className="bg-[#1B4332] hover:bg-[#2D6A4F]" data-testid="generate-invoices-btn">
            {generating ? "Generating…" : "Generate invoices"}
          </Button>
        </CardContent>
      </Card>

      {/* Filter chips */}
      <div className="flex items-center gap-2 flex-wrap">
        {[
          { k: "active", label: "Active" },
          { k: "pending", label: "Pending" },
          { k: "ended", label: "Ended" },
          { k: "terminated", label: "Terminated" },
          { k: "all", label: "All" },
        ].map(({ k, label }) => (
          <button key={k} onClick={() => setFilter(k)}
            data-testid={`filter-${k}`}
            className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${
              filter === k
                ? "bg-[#1B4332] text-white border-[#1B4332]"
                : "bg-white text-[#525C58] border-[#E8E8E6] hover:bg-[#F4F4F1]"
            }`}>
            {label} ({counts[k] || 0})
          </button>
        ))}
      </div>

      <Card className="border-[#E8E8E6]">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-[#40916C]" />
            Leases — {filter} ({filtered.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filtered.length === 0 ? (
            <p className="text-sm text-[#7A8A84] text-center py-10">
              {filter === "active" ? "No active leases. Click 'New lease' to set up the first one." : `No ${filter} leases.`}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tenant</TableHead>
                    <TableHead>Unit</TableHead>
                    <TableHead>Term</TableHead>
                    <TableHead>Rent</TableHead>
                    <TableHead>Add-ons</TableHead>
                    <TableHead>Deposit</TableHead>
                    <TableHead>Bill day</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((l) => {
                    const rentMonthly = Number(l.monthly_rent || 0);
                    const addOnsTotal = (l.add_ons || []).reduce((s, a) => s + (Number(a.amount) || 0), 0);
                    return (
                      <TableRow key={l.id} data-testid={`lease-row-${l.id}`}>
                        <TableCell>
                          <div className="font-medium">{l.tenant_name}</div>
                          {l.tenant_phone && <p className="text-[10px] text-[#7A8A84]">{l.tenant_phone}</p>}
                          {l.tenant_email && <p className="text-[10px] text-[#7A8A84]">{l.tenant_email}</p>}
                        </TableCell>
                        <TableCell className="text-sm">{l.unit_name || "—"}</TableCell>
                        <TableCell className="text-sm">
                          {l.start_date} →<br />
                          <span className="text-[#7A8A84]">{l.end_date || "open-ended"}</span>
                        </TableCell>
                        <TableCell className="text-sm tabular-nums">
                          {l.rent_currency} {rentMonthly.toLocaleString()}
                          <p className="text-[9px] text-[#7A8A84]">/ month</p>
                        </TableCell>
                        <TableCell className="text-sm">
                          {(l.add_ons || []).length === 0 ? <span className="text-[#7A8A84]">—</span> : (
                            <div className="space-y-0.5">
                              {(l.add_ons || []).slice(0, 3).map((a, i) => (
                                <div key={i} className="text-[11px]">{a.label}: {a.currency} {Number(a.amount).toLocaleString()}</div>
                              ))}
                              {(l.add_ons || []).length > 3 && <div className="text-[10px] text-[#7A8A84]">+{l.add_ons.length - 3} more</div>}
                              <div className="text-[10px] font-medium text-[#1B4332]">+{l.rent_currency} {addOnsTotal.toLocaleString()}/m</div>
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-sm tabular-nums">
                          {l.deposit_amount ? `${l.deposit_currency} ${Number(l.deposit_amount).toLocaleString()}` : "—"}
                          <p className="text-[9px] text-[#7A8A84]">{l.deposit_status || ""}</p>
                        </TableCell>
                        <TableCell className="text-sm">{l.billing_day || 1}</TableCell>
                        <TableCell>
                          <Badge className={`${STATUS_BADGE[l.status] || ""} border text-xs`}>{l.status}</Badge>
                          {l.shared_unit && <Badge className="ml-1 bg-purple-100 text-purple-800 border border-purple-300 text-[9px] uppercase">Shared</Badge>}
                        </TableCell>
                        <TableCell className="text-right space-x-1 whitespace-nowrap">
                          <Button size="sm" variant="ghost" onClick={() => downloadStatement(l)} className="text-[#1B4332]" title="Download statement PDF" data-testid={`statement-pdf-${l.id}`}>
                            <FileDown className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => openSendStatement(l)} className="text-emerald-700" title="Email statement" data-testid={`statement-send-${l.id}`}>
                            <Send className="h-3.5 w-3.5" />
                          </Button>
                          {canInvite && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openInvite(l)}
                              className={
                                portalStatus[l.id]?.has_login
                                  ? "text-emerald-700 hover:bg-emerald-50"
                                  : portalStatus[l.id]?.invite_pending
                                  ? "text-amber-700 hover:bg-amber-50"
                                  : "text-blue-700 hover:bg-blue-50"
                              }
                              title={
                                portalStatus[l.id]?.has_login
                                  ? "Tenant has portal login — re-invite to reset"
                                  : portalStatus[l.id]?.invite_pending
                                  ? "Invite pending — re-send if needed"
                                  : "Invite tenant to portal"
                              }
                              data-testid={`invite-tenant-${l.id}`}
                            >
                              {portalStatus[l.id]?.has_login ? (
                                <UserCheck className="h-3.5 w-3.5" />
                              ) : (
                                <UserPlus className="h-3.5 w-3.5" />
                              )}
                            </Button>
                          )}
                          {l.status !== "ended" && l.status !== "terminated" && (
                            <Button size="sm" variant="ghost" onClick={() => setEditing({ ...l, add_ons: (l.add_ons || []).map((a) => ({ ...a })) })}
                              className="text-[#1B4332]" data-testid={`edit-lease-${l.id}`}>
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          {l.status === "active" && (
                            <Button size="sm" variant="ghost" onClick={() => endLease(l)} className="text-amber-700" title="End normally" data-testid={`end-lease-${l.id}`}>
                              <StopCircle className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          {isAdmin && (
                            <Button size="sm" variant="ghost" onClick={() => deleteLease(l)} className="text-red-600" title="Hard delete" data-testid={`delete-lease-${l.id}`}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create / edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Edit lease" : "New lease"}</DialogTitle>
            <DialogDescription>Long-term residency billed monthly. The unit calendar will reject conflicting visitor bookings.</DialogDescription>
          </DialogHeader>

          {editing && (
            <div className="space-y-5">
              {/* Tenant */}
              <Card className="border-[#E8E8E6]">
                <CardHeader className="pb-3"><CardTitle className="text-sm flex items-center gap-2"><IdCard className="h-4 w-4" /> Tenant</CardTitle></CardHeader>
                <CardContent className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label>Full name *</Label>
                    <Input value={editing.tenant_name} onChange={(e) => setEditing({ ...editing, tenant_name: e.target.value })} data-testid="tenant-name-input" />
                  </div>
                  <div className="space-y-1">
                    <Label className="flex items-center gap-1"><Phone className="h-3 w-3" /> Phone</Label>
                    <Input value={editing.tenant_phone} onChange={(e) => setEditing({ ...editing, tenant_phone: e.target.value })} data-testid="tenant-phone-input" />
                  </div>
                  <div className="space-y-1">
                    <Label className="flex items-center gap-1"><Mail className="h-3 w-3" /> Email</Label>
                    <Input type="email" value={editing.tenant_email} onChange={(e) => setEditing({ ...editing, tenant_email: e.target.value })} data-testid="tenant-email-input" />
                  </div>
                  <div className="space-y-1">
                    <Label>ID / Passport</Label>
                    <Input value={editing.tenant_id_number} onChange={(e) => setEditing({ ...editing, tenant_id_number: e.target.value })} data-testid="tenant-id-input" />
                  </div>
                </CardContent>
              </Card>

              {/* Unit + dates */}
              <Card className="border-[#E8E8E6]">
                <CardHeader className="pb-3"><CardTitle className="text-sm flex items-center gap-2"><CalendarRange className="h-4 w-4" /> Unit & term</CardTitle></CardHeader>
                <CardContent className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label>Unit *</Label>
                    <Select value={editing.unit_id} onValueChange={(v) => setEditing({ ...editing, unit_id: v })}>
                      <SelectTrigger data-testid="unit-select"><SelectValue placeholder="Pick a unit" /></SelectTrigger>
                      <SelectContent>
                        {units.filter((u) => u.active).map((u) => (
                          <SelectItem key={u.id} value={u.id}>{u.name} (cap {u.capacity})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label>Status</Label>
                    <Select value={editing.status} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                      <SelectTrigger data-testid="lease-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label>Start date *</Label>
                    <Input type="date" value={editing.start_date} onChange={(e) => setEditing({ ...editing, start_date: e.target.value })} data-testid="start-date-input" />
                  </div>
                  <div className="space-y-1">
                    <Label>End date <span className="text-[10px] text-[#7A8A84]">(blank = open-ended)</span></Label>
                    <Input type="date" value={editing.end_date || ""} onChange={(e) => setEditing({ ...editing, end_date: e.target.value })} data-testid="end-date-input" />
                  </div>
                </CardContent>
              </Card>

              {/* Rent + deposit */}
              <Card className="border-[#E8E8E6]">
                <CardHeader className="pb-3"><CardTitle className="text-sm flex items-center gap-2"><Wallet className="h-4 w-4" /> Rent & deposit</CardTitle></CardHeader>
                <CardContent className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label>Rate card</Label>
                    <Select value={editing.rate_card_id || "__custom__"} onValueChange={(v) => onPickRateCard(v === "__custom__" ? null : v)}>
                      <SelectTrigger data-testid="rate-card-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__custom__">Custom (manual amount)</SelectItem>
                        {rateCards.filter((r) => r.active).map((r) => (
                          <SelectItem key={r.id} value={r.id}>{r.name} — {r.currency} {r.amount.toLocaleString()}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label>Monthly rent</Label>
                      <Input type="number" value={editing.monthly_rent} onChange={(e) => setEditing({ ...editing, monthly_rent: Number(e.target.value) })} data-testid="monthly-rent-input" />
                    </div>
                    <div className="space-y-1">
                      <Label>Currency</Label>
                      <Select value={editing.rent_currency} onValueChange={(v) => setEditing({ ...editing, rent_currency: v })}>
                        <SelectTrigger data-testid="rent-currency-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="KES">KES</SelectItem>
                          <SelectItem value="USD">USD</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label>Deposit</Label>
                      <Input type="number" value={editing.deposit_amount} onChange={(e) => setEditing({ ...editing, deposit_amount: Number(e.target.value) })} data-testid="deposit-amount-input" />
                    </div>
                    <div className="space-y-1">
                      <Label>Status</Label>
                      <Select value={editing.deposit_status} onValueChange={(v) => setEditing({ ...editing, deposit_status: v })}>
                        <SelectTrigger data-testid="deposit-status-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="held">Held</SelectItem>
                          <SelectItem value="partial">Partial</SelectItem>
                          <SelectItem value="refunded">Refunded</SelectItem>
                          <SelectItem value="waived">Waived</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label>Billing day of month <span className="text-[10px] text-[#7A8A84]">(1-28)</span></Label>
                    <Input type="number" min={1} max={28} value={editing.billing_day} onChange={(e) => setEditing({ ...editing, billing_day: Math.max(1, Math.min(28, Number(e.target.value) || 1)) })} data-testid="billing-day-input" />
                  </div>
                </CardContent>
              </Card>

              {/* Recurring add-ons */}
              <Card className="border-[#E8E8E6]">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center justify-between flex-wrap gap-2">
                    <span className="flex items-center gap-2"><Receipt className="h-4 w-4" /> Monthly add-ons</span>
                    <div className="flex gap-1 flex-wrap">
                      {QUICK_ADD_ONS.map((label) => (
                        <Button key={label} type="button" size="sm" variant="outline"
                          onClick={() => addAddOn(label)}
                          className="h-7 text-[11px] border-[#1B4332]/20"
                          data-testid={`quick-addon-${label.toLowerCase().replace(/\s+/g, "-")}`}>
                          + {label}
                        </Button>
                      ))}
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {editing.add_ons.length === 0 && (
                    <p className="text-xs text-[#7A8A84] py-4 text-center border border-dashed border-[#E8E8E6] rounded-lg">
                      Tap a quick-add button above (Utilities, Trash, Security, House helper) to add a recurring monthly charge.
                    </p>
                  )}
                  {editing.add_ons.map((a, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-end" data-testid={`addon-row-${idx}`}>
                      <div className="col-span-4 space-y-1">
                        <Label className="text-[10px] uppercase text-[#7A8A84]">Label</Label>
                        <Input value={a.label} onChange={(e) => updateAddOn(idx, { label: e.target.value })} />
                      </div>
                      <div className="col-span-3 space-y-1">
                        <Label className="text-[10px] uppercase text-[#7A8A84]">Amount / month</Label>
                        <Input type="number" value={a.amount} onChange={(e) => updateAddOn(idx, { amount: Number(e.target.value) })} />
                      </div>
                      <div className="col-span-2 space-y-1">
                        <Label className="text-[10px] uppercase text-[#7A8A84]">Currency</Label>
                        <Select value={a.currency} onValueChange={(v) => updateAddOn(idx, { currency: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="KES">KES</SelectItem>
                            <SelectItem value="USD">USD</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2 space-y-1">
                        <Label className="text-[10px] uppercase text-[#7A8A84]">Notes</Label>
                        <Input value={a.notes || ""} onChange={(e) => updateAddOn(idx, { notes: e.target.value })} />
                      </div>
                      <div className="col-span-1">
                        <Button variant="ghost" size="sm" onClick={() => removeAddOn(idx)} className="text-red-600">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  <div className="text-right text-sm font-semibold text-[#1B4332] pt-2 border-t border-[#E8E8E6]" data-testid="lease-monthly-total">
                    Monthly total: {editing.rent_currency} {totalMonthly.toLocaleString()}
                  </div>
                </CardContent>
              </Card>

              {/* Conflict feedback */}
              {conflict && (
                <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-800 space-y-2" data-testid="lease-conflict">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                    <span>{conflict}</span>
                  </div>
                  <label className="flex items-center gap-2 text-red-900 font-medium cursor-pointer">
                    <input type="checkbox" checked={!!editing.allow_override}
                      onChange={(e) => setEditing({ ...editing, allow_override: e.target.checked })}
                      data-testid="allow-override-checkbox" />
                    Override & allow unit sharing
                  </label>
                  {editing.allow_override && (
                    <Input placeholder="Reason for override (required)" value={editing.override_reason || ""}
                      onChange={(e) => setEditing({ ...editing, override_reason: e.target.value })}
                      className="bg-white border-red-200" data-testid="override-reason-input" />
                  )}
                </div>
              )}

              <div className="space-y-1">
                <Label>Notes</Label>
                <Textarea rows={2} value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} data-testid="lease-cancel-btn">
              <X className="h-4 w-4 mr-2" /> Cancel
            </Button>
            <Button onClick={saveLease} className="bg-[#1B4332] hover:bg-[#2D6A4F]" data-testid="lease-save-btn">
              <Check className="h-4 w-4 mr-2" /> Save lease
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Send statement dialog */}
      <Dialog open={!!sendStatementOpen} onOpenChange={(o) => !o && setSendStatementOpen(null)}>
        <DialogContent className="max-w-lg" data-testid="send-statement-dialog">
          <DialogHeader>
            <DialogTitle>Email tenant statement</DialogTitle>
            <DialogDescription>
              {sendStatementOpen && <>Statement of invoices and payments for <b>{sendStatementOpen.tenant_name}</b></>}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>From</Label>
                <Input type="date" value={stmtFrom} onChange={(e) => setStmtFrom(e.target.value)} data-testid="stmt-from-input" />
              </div>
              <div className="space-y-1">
                <Label>To</Label>
                <Input type="date" value={stmtTo} onChange={(e) => setStmtTo(e.target.value)} data-testid="stmt-to-input" />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Recipients (comma-separated)</Label>
              <Textarea rows={2} value={stmtRecipients} onChange={(e) => setStmtRecipients(e.target.value)} data-testid="stmt-recipients-input" />
            </div>
            <div className="space-y-1">
              <Label>Note (optional)</Label>
              <Textarea rows={2} value={stmtNote} onChange={(e) => setStmtNote(e.target.value)} placeholder="Hi — please find your statement attached…" data-testid="stmt-note-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendStatementOpen(null)}>Cancel</Button>
            <Button onClick={sendStatement} disabled={sendingStmt} className="bg-[#40916C] hover:bg-[#2D6A4F]" data-testid="confirm-stmt-send-btn">
              <Send className="h-4 w-4 mr-2" /> {sendingStmt ? "Sending…" : "Send"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Phase 46 — Invite tenant to portal ─── */}
      <Dialog open={!!inviteOpen} onOpenChange={(o) => !o && setInviteOpen(null)}>
        <DialogContent className="max-w-lg" data-testid="invite-tenant-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-blue-700" /> Invite tenant to portal
            </DialogTitle>
            <DialogDescription>
              {inviteOpen && (
                <>Send <strong>{inviteOpen.tenant_name}</strong> a one-time link to set a password and access the tenant portal — they'll be able to view their lease, invoices and payments, download invoice PDFs, and upload payment proofs.</>
              )}
            </DialogDescription>
          </DialogHeader>
          {inviteOpen && (
            <div className="space-y-4">
              {/* Current portal status pill */}
              {portalStatus[inviteOpen.id]?.has_login && (
                <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-900" data-testid="invite-status-active">
                  <UserCheck className="h-4 w-4 shrink-0" />
                  <span>This tenant already has portal access. Sending a new invite revokes their existing password — only do this if they've forgotten it.</span>
                </div>
              )}
              {portalStatus[inviteOpen.id]?.invite_pending && !portalStatus[inviteOpen.id]?.has_login && (
                <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900" data-testid="invite-status-pending">
                  <RefreshCw className="h-4 w-4 shrink-0" />
                  <span>An invite is already outstanding (expires {(portalStatus[inviteOpen.id].invite_expires_at || "").slice(0, 10)}). Re-sending will revoke the previous link.</span>
                </div>
              )}

              {!inviteResult && (
                <>
                  <div className="space-y-1">
                    <Label htmlFor="invite-email">Tenant email</Label>
                    <Input
                      id="invite-email"
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder="tenant@example.org"
                      data-testid="invite-email-input"
                    />
                    <p className="text-[11px] text-[#7A8A84]">Defaults to the email saved on the lease. The tenant gets a 7-day link.</p>
                  </div>
                </>
              )}

              {inviteResult && (
                <div className="space-y-3" data-testid="invite-result">
                  <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-900">
                    <Check className="h-4 w-4 shrink-0" />
                    <span className="flex-1">
                      Invite created for <strong>{inviteResult.email}</strong>.
                      {inviteResult.email_sent ? " Email sent." : inviteResult.mocked ? " Email is mocked on this server — share the link manually." : ""}
                    </span>
                  </div>
                  {inviteResult.link && (
                    <div className="space-y-1">
                      <Label className="text-xs">Set-password link (expires {(inviteResult.expires_at || "").slice(0, 10)})</Label>
                      <div className="flex gap-2">
                        <Input value={inviteResult.link} readOnly className="font-mono text-xs" data-testid="invite-link-input" />
                        <Button onClick={copyInviteLink} variant="outline" className="shrink-0" data-testid="invite-copy-link-btn">
                          <Copy className="h-3.5 w-3.5 mr-1" /> Copy
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteOpen(null)}>Close</Button>
            {!inviteResult && (
              <Button onClick={sendInvite} disabled={inviting} className="bg-blue-700 hover:bg-blue-800" data-testid="confirm-invite-btn">
                <Send className="h-4 w-4 mr-2" /> {inviting ? "Sending…" : "Send invite"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
